/**
 * BlinterestPinForm — single moderation-queue tile.
 * Presentational: publish/remove/CTA update callbacks come from the parent.
 */
import { Trash2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { resolveAssetUrl } from "@/lib/assetUrl";

export default function BlinterestPinForm({ pin: p, onPublish, onRemove, onUpdateCta }) {
  return (
    <div
      className="rounded-xl overflow-hidden bg-gray-50 border border-gray-100"
      data-testid={`admin-pin-${p.pin_id}`}
    >
      <Link to={`/blinterest/pin/${p.pin_id}`}>
        <img
          src={resolveAssetUrl(p.image_url)}
          alt={p.title}
          className="w-full aspect-square object-cover"
          loading="lazy"
        />
      </Link>
      <div className="p-2 space-y-1">
        <p className="text-xs font-body text-tbc-navy line-clamp-2 min-h-[2rem]">{p.title}</p>
        <p className="text-[10px] text-gray-500 truncate">{p.submission_source}</p>
        {p.destination_url && (
          <a
            href={p.destination_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] text-tbc-navy inline-flex items-center gap-0.5 hover:underline"
          >
            source
            <ExternalLink className="w-2.5 h-2.5" />
          </a>
        )}
        <select
          value={p.cta_type || ""}
          onChange={(e) => onUpdateCta(p.pin_id, e.target.value)}
          className="w-full h-7 border rounded px-1 text-[10px] text-tbc-navy bg-white"
          data-testid={`cta-select-${p.pin_id}`}
        >
          <option value="">Smart default</option>
          <option value="shop_business">Shop This Business</option>
          <option value="book_now">Book Now</option>
          <option value="shop_product">Shop the Look</option>
          <option value="visit_website">Visit Site</option>
          <option value="none">No secondary CTA</option>
        </select>
        <div className="flex gap-1 pt-1">
          {p.status !== "published" && (
            <Button
              size="sm"
              onClick={() => onPublish(p.pin_id)}
              className="flex-1 h-7 text-xs bg-tbc-navy text-white"
              data-testid={`publish-${p.pin_id}`}
            >
              Publish
            </Button>
          )}
          {p.status !== "removed" && (
            <Button
              size="sm"
              onClick={() => onRemove(p.pin_id)}
              variant="outline"
              className="h-7 text-xs border-red-200 text-red-600 hover:bg-red-50"
              data-testid={`remove-${p.pin_id}`}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
